import { RootState } from '@next/core-lib/app';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';
import { SettingsDataObject } from '@next/core-lib/settings';

export interface FarmerInsightsModuleState extends RootState {
  timeSeries: DataSeries<number>;
  benchmarkTimeSeries: DataSeries<number>;
  benchmarkLastWeeks: DataSeries<number>;
  benchmarkSelectedPeriodData: BenchmarkData;
  benchmarkLastWeeksData: BenchmarkData;
  benchmarkCustomerAverageSelectedPeriodData: BenchmarkData;
  queryableFlockData: QueryableFlockData;
  normalizedTimeSeriesArray: NormalizedTimeSeries[];
  selectedDateRange: DateRange;
  selectedKpis: FlockDataParameter[];
  isDetailedModeSelected: boolean;
  selectedViewMode: ViewMode;
  selectedLocationIds: number[];
  unselectedCycleIds: number[];
  selectedBarnIds: number[];
  selectedDataViewType: DataViewType;
  selectedConceptId: number;
  isLoaded: boolean;
  benchmarkSettings: BenchmarkSettings;
  localFilterSettings: LocalFilterSettings;
  globalFilterSettings: GlobalFilterSettings;
  kpiGroups: KpiGroup[];
}

export interface GetFlockDataRequest {
  startDate?: string;
  endDate?: string;
  locationIds: number[];
  cycleIds: number[];
  barnIds: number[];
  parameters: FlockDataParameter[];
  dataViewType: DataViewType;
  conceptId: number;
}

export interface GetBenchmarksRequest {
  startDate: string;
  endDate: string;
  parameters: FlockDataParameter[];
  conceptId: number;
}

export interface TimeSeriesResponse {
  data: DataSeries<number>;
}

export enum ViewMode {
  Table = 'Table',
  Chart = 'Chart',
}

export enum DataViewType {
  PerCycleBarn = 'PerCycleBarn',
  PerCycle = 'PerCycle',
  PerLocation = 'PerLocation',
  PerLocationBarn = 'PerLocationBarn',
}

export enum AggregationType {
  Sum = 'Sum',
  Average = 'Average',
  Max = 'Max',
  Min = 'Min',
}

export enum FlockDataParameter {
  DayOldChicks = 'DayOldChicks',
  DayOldChicksWeight = 'DayOldChicksWeight',
  DayOldChicksPrice = 'DayOldChicksPrice',
  DayOldChicksPriceExclDiscounts = 'DayOldChicksPriceExclDiscounts',
  TotalDeparture = 'TotalDeparture',
  PartialDeparture = 'PartialDeparture',
  FullDeparture = 'FullDeparture',
  DepartureWeight = 'DepartureWeight',
  SlaughterWeight = 'SlaughterWeight',
  Revenue = 'Revenue',
  RevenueExclDiscounts = 'RevenueExclDiscounts',
  FeedBalance = 'FeedBalance',
  FeedBalancePrice = 'FeedBalancePrice',
  FeedBalancePriceExclDiscounts = 'FeedBalancePriceExclDiscounts',
  FeedBalanceRawProduct = 'FeedBalanceRawProduct',
  FeedBalanceMixProduct = 'FeedBalanceMixProduct',
  FeedBalanceRawPrice = 'FeedBalanceRawPrice',
  FeedBalanceMixPrice = 'FeedBalanceMixPrice',
  WeightedHeelDermatitis = 'WeightedHeelDermatitis',
  WeightedFootpadLesionNonePercentage = 'WeightedFootpadLesionNonePercentage',
  WeightedFootpadLesionModeratePercentage = 'WeightedFootpadLesionModeratePercentage',
  WeightedFootpadLesionSeverePercentage = 'WeightedFootpadLesionSeverePercentage',
  TotalLoss = 'TotalLoss',
  LossPercentage = 'LossPercentage',
  WeightedFootpadLesionScore = 'WeightedFootpadLesionScore',
  ReplacementPercentage = 'ReplacementPercentage',
  FeedConversion = 'FeedConversion',
  FeedConversion1500 = 'FeedConversion1500',
  AverageWeightInGram = 'AverageWeightInGram',
  WeightedAverageAgeInDays = 'WeightedAverageAgeInDays',
  WeightedAverageGrowthPerDay = 'WeightedAverageGrowthPerDay',
  ProductionNumber = 'ProductionNumber',
  DayOldChicksPerSquareMeter = 'DayOldChicksPerSquareMeter',
  FeedUsageInKgPerSquareMeter = 'FeedUsageInKgPerSquareMeter',
  PricePerDayOldChick = 'PricePerDayOldChick',
  FeedConsumptionPricePer100Kg = 'FeedConsumptionPricePer100Kg',
  PaidAmountPerKg = 'PaidAmountPerKg',
  NettoRevenue = 'NettoRevenue',
  NettoRevenuePerChick = 'NettoRevenuePerChick',
  NettoRevenuePerSquareMeter = 'NettoRevenuePerSquareMeter',
  NettoRevenuePerSquareMeterPerDay = 'NettoRevenuePerSquareMeterPerDay',
  FeedConsumptionPerDeliveredKg = 'FeedConsumptionPerDeliveredKg',
  RejectionRate = 'RejectionRate',
  DeliveredKgPerSquareMeter = 'DeliveredKgPerSquareMeter',
  NettoProfitInclOtherCosts = 'NettoProfitInclOtherCosts',
}

export interface SelectionListItem<ValueType> {
  name: string;
  selected: boolean;
  value: ValueType;
}

export interface CycleSelectionListItem<ValueType> {
  cycle: string;
  location: string;
  name: string;
  selected: boolean;
  value: ValueType;
}

export interface NormalizedTimeSeries {
  cycleId?: number;
  cycleNumber?: number;
  barnId?: number;
  barnName?: string;
  breeds?: string;
  kpiValues: NormalizedValue[];
  locationId?: number;
  locationName?: string;
  fromDate?: Date;
  toDate?: Date;
}

export interface NormalizedValue {
  parameter: DataSeriesParameter;
  value: number;
  dateTime: Date;
}

export interface QueryableFlockData {
  locations: QueryableLocation[];
  cycles: QueryableCycle[];
  barns: QueryableBarn[];
  concepts: QueryableConcept[];
  parameters: DataSeriesParameter[];
}

export interface QueryableLocation {
  locationId: number;
  locationName: string;
  cycleIds: number[];
}

export interface QueryableCycle {
  cycleId: number;
  conceptId: number;
  cycleNumber: number;
  locationId: number;
  locationName: string;
  cycleDate?: string;
  cycleEndDate?: string;
  barnIds: number[];
}

export interface QueryableBarn {
  barnId: number;
  barnName: string;
}

export interface QueryableConcept {
  conceptId: number;
  name: string;
}

export interface NormalizedQueryableLocation {
  locationId: number;
  locationName: string;
  cycles: NormalizedQueryableCycle[];
}

export interface NormalizedQueryableCycle {
  cycleId: number;
  cycleNumber: number;
  locationId: number;
  locationName: string;
  cycleDate?: string;
  cycleEndDate?: string;
  barns: QueryableBarn[];
}

export interface BenchmarkData {
  flocksUsed: number;
  benchmarks: Benchmark[];
}

export interface Benchmark {
  parameter: DataSeriesParameter;
  value: number;
}

export interface BenchmarkSettings extends SettingsDataObject {
  selectedNonDetailedBenchmarkType: BenchmarkTypes;
  selectedPeriod: boolean;
  lastWeeks: boolean;
  customerAverageSelectedPeriod: boolean;
}

export interface LocalFilterSettings extends SettingsDataObject {
  selectedLocationIds: number[];
  unselectedCycleIds: number[];
  selectedBarnIds: number[];
  selectedConceptId: number;
  selectedDateRangeStart: string;
  selectedDateRangeEnd: string;
  selectedDateRangeDescription: string;
}

export interface GlobalFilterSettings extends SettingsDataObject {
  selectedDataViewType?: DataViewType;
  selectedKpis: FlockDataParameter[];
  isDetailedModeSelected: boolean;
  selectedViewMode: ViewMode;
}

export interface DateRange {
  description?: string;
  startDate: string;
  endDate: string;
}

export interface KpiGroup {
  name: string;
  children: KpiGroupingChild[];
  order: number;
}

export interface KpiGroupingChild {
  kpi: FlockDataParameter;
  order: number;
}

export const DefaultKPIselection = [
  FlockDataParameter.DayOldChicks,
  FlockDataParameter.PricePerDayOldChick,
  FlockDataParameter.LossPercentage,
  FlockDataParameter.FeedConversion,
  FlockDataParameter.AverageWeightInGram,
  FlockDataParameter.WeightedAverageGrowthPerDay,
  FlockDataParameter.FeedConsumptionPricePer100Kg,
  FlockDataParameter.PaidAmountPerKg,
  FlockDataParameter.NettoRevenuePerChick,
  FlockDataParameter.RejectionRate,
  FlockDataParameter.ReplacementPercentage,
];

export enum BenchmarkTypes {
  None = 'none',
  SelectedPeriod = 'selectedPeriod',
  LastWeeks = 'lastWeeks',
  CustomerAverageSelectedPeriod = 'customerAverageSelectedPeriod',
}
